import posthog, { type Properties } from "posthog-js";
import { getCookieValue } from "../utils";

let enabled: boolean = false;

const setupPostHog = (shop: string) => {
  const posthogId = import.meta.env.VITE_POSTHOG_ID;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

  const disableTracking = getCookieValue("disable_tracking");

  if (process.env.NODE_ENV !== "development" && disableTracking === null) {
    posthog.init(posthogId, {
      api_host: posthogHost,
      autocapture: false,
      ui_host: "https://utils.pina.earth",
    });
    enabled = true;

    posthog.group("shop", shop);
  }
};

export type PostHogModalInteractionEvent =
  | "accordion_opened"
  | "accordion_closed"
  | "opened_registry_link";

export type PostHogComponentDisplayEvent =
  | "badge_displayed"
  | "button_displayed"
  | "modal_displayed"
  | "banner_displayed";

export type PostHogComponentViewportEvent =
  | "badge_in_viewport"
  | "button_in_viewport"
  | "modal_in_viewport";

export type PostHogEvent =
  | "modal_opened_by_badge"
  | "modal_opened_by_button"
  | "modal_closed"
  | "offset_selected"
  | "offset_deselected"
  | "offset_added_to_cart"
  | "offset_removed_from_cart"
  | "pageview"
  | "purchase_without_offset"
  | "purchase_with_offset"
  | PostHogComponentDisplayEvent
  | PostHogComponentViewportEvent
  | PostHogModalInteractionEvent;

export const capturePHEvent = (
  event: PostHogEvent | undefined,
  properties?: Properties,
) => {
  try {
    if (enabled && event) {
      posthog.capture(event, properties);
    }
  } catch (e) {
    console.error(e);
  }
};

export const capturePHComponentDisplayEvent = (
  element: HTMLElement,
  event: PostHogComponentDisplayEvent,
  properties?: Properties,
) => {
  const isElementVisible = (element: HTMLElement) =>
    element.getClientRects().length > 0;

  const interval = setInterval(() => {
    if (isElementVisible(element)) {
      capturePHEvent(event, properties);
      clearInterval(interval);
    }
  }, 250);
};

export const capturePHComponentInViewportEvent = (
  element: HTMLElement,
  event: PostHogComponentViewportEvent,
  properties?: Properties,
) => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        const rect = entry.boundingClientRect;
        if (rect.width <= 0 || rect.height <= 0) return;

        capturePHEvent(event, properties);
        observer.unobserve(element);
      }
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0.9,
    },
  );

  observer.observe(element);
};

export default setupPostHog;
